/**
 * Shared scaffolding for the AdminSettings overhaul:
 *   - Stitch design tokens (T) + Icon helper
 *   - SettingsProvider / useSettings — fetches /admin/settings/defaults + /
 *     and exposes save/reset/updateValue helpers used by every section.
 *   - SettingField + KeyValueEditor — common UI primitives.
 */
import React, {
  createContext, useCallback, useContext, useEffect, useMemo, useState,
} from 'react';
import {
  Box, Typography, Paper, TextField, Button, Chip, CircularProgress,
  Accordion, AccordionSummary, AccordionDetails,
} from '@mui/material';
import {
  Save as SaveIcon, RestartAlt as ResetIcon, ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import toast from 'react-hot-toast';
import { useMsal } from '@azure/msal-react';
import { AI_ENGINE_URL } from '../../utils/apiConfig';
import { authFetch } from '../../utils/authHelpers';

export const T = {
  primary: '#526075', primaryDim: '#465469',
  tertiary: '#635983', tertiaryContainer: '#dbcdff',
  primaryContainer: '#d5e3fd',
  bg: '#f8f9fa',
  surface: '#ffffff', surfaceLow: '#f1f4f5', surfaceHigh: '#e5e9eb',
  text: '#2d3335', secondary: '#556071',
  outline: '#767c7e', outlineVariant: '#adb3b5',
  emerald: '#10b981', emeraldBg: '#ecfdf5',
  amber: '#f59e0b', amberBg: '#fef3c7',
  rose: '#ec4899', roseBg: '#fce7f3',
  violet: '#7c3aed', violetBg: '#ede9fe',
  blue: '#3b82f6', blueBg: '#dbeafe',
  shadow: '0 1px 3px rgba(45,51,53,0.04)',
  font: '"Manrope", system-ui, sans-serif',
  fontBody: '"Inter", system-ui, sans-serif',
};

export const Icon = ({ name, size = 22, fill, sx = {} }) => (
  <span
    className="material-symbols-outlined"
    style={{
      fontSize: size,
      fontVariationSettings: `'FILL' ${fill ? 1 : 0}, 'wght' 500, 'GRAD' 0, 'opsz' ${size}`,
      lineHeight: 1,
      ...sx,
    }}
  >
    {name}
  </span>
);

const SETTINGS_URL = `${AI_ENGINE_URL}/api/v1/admin/settings`;

const SettingsContext = createContext(null);

export function SettingsProvider({ children }) {
  const { instance } = useMsal();
  const userEmail = instance.getActiveAccount()?.username || 'unknown';

  const [values, setValues] = useState({});
  const [dbKeys, setDbKeys] = useState(new Set());
  const [defaults, setDefaults] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState({});

  const fetchData = useCallback(async () => {
    setLoading(true);
    try {
      const [settingsRes, defaultsRes] = await Promise.all([
        authFetch(`${SETTINGS_URL}/`),
        authFetch(`${SETTINGS_URL}/defaults`),
      ]);
      const settingsData = await settingsRes.json();
      const defaultsData = await defaultsRes.json();
      const defs = defaultsData.defaults || {};
      setDefaults(defs);

      const currentValues = {};
      for (const [key, def] of Object.entries(defs)) {
        currentValues[key] = def.value;
      }
      const dbKeySet = new Set();
      for (const s of settingsData.settings || []) {
        currentValues[s.key] = s.value;
        dbKeySet.add(s.key);
      }
      setValues(currentValues);
      setDbKeys(dbKeySet);
    } catch (err) {
      console.error('[AdminSettings] fetch error:', err);
      toast.error('Fout bij laden instellingen');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchData(); }, [fetchData]);

  const save = useCallback(async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await authFetch(`${SETTINGS_URL}/${key}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ value: values[key], updated_by: userEmail }),
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setDbKeys(prev => new Set([...prev, key]));
      toast.success(`${key} opgeslagen`);
    } catch (err) {
      toast.error(`Opslaan mislukt: ${err.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  }, [values, userEmail]);

  const reset = useCallback(async (key) => {
    setSaving(prev => ({ ...prev, [key]: true }));
    try {
      const res = await authFetch(`${SETTINGS_URL}/reset/${key}`, { method: 'POST' });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      setValues(prev => ({ ...prev, [key]: defaults[key]?.value }));
      setDbKeys(prev => { const next = new Set(prev); next.delete(key); return next; });
      toast.success(`${key} teruggezet naar standaard`);
    } catch (err) {
      toast.error(`Reset mislukt: ${err.message}`);
    } finally {
      setSaving(prev => ({ ...prev, [key]: false }));
    }
  }, [defaults]);

  const updateValue = useCallback((key, val) => {
    setValues(prev => ({ ...prev, [key]: val }));
  }, []);

  const ctx = useMemo(() => ({
    values, defaults, dbKeys, loading, saving,
    isCustomized: (key) => dbKeys.has(key),
    save, reset, updateValue,
  }), [values, defaults, dbKeys, loading, saving, save, reset, updateValue]);

  return <SettingsContext.Provider value={ctx}>{children}</SettingsContext.Provider>;
}

export function useSettings() {
  const ctx = useContext(SettingsContext);
  if (!ctx) throw new Error('useSettings must be inside <SettingsProvider>');
  return ctx;
}

export function SettingField({
  settingKey, label, type = 'text', multiline = false, rows = 1, helperText = '',
}) {
  const { values, isCustomized, saving, save, reset, updateValue } = useSettings();
  const isCust = isCustomized(settingKey);
  const isSaving = !!saving[settingKey];
  return (
    <Paper sx={{ p: 2.5, mb: 2 }} variant="outlined">
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
        <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: T.font }}>{label}</Typography>
        {isCust && <Chip label="Aangepast" size="small" color="info" variant="outlined" />}
      </Box>
      {helperText && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
          {helperText}
        </Typography>
      )}
      <TextField
        fullWidth size="small" type={type} multiline={multiline} rows={rows}
        value={values[settingKey] ?? ''}
        onChange={(e) => updateValue(settingKey, type === 'number' ? Number(e.target.value) : e.target.value)}
        sx={{
          mb: 1.5,
          '& .MuiInputBase-root': {
            fontFamily: multiline ? 'monospace' : 'inherit',
            fontSize: multiline ? '0.85rem' : 'inherit',
          },
        }}
      />
      <Box sx={{ display: 'flex', gap: 1 }}>
        <Button size="small" variant="contained"
          startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
          onClick={() => save(settingKey)} disabled={isSaving}
          sx={{ bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDim } }}>
          Opslaan
        </Button>
        {isCust && (
          <Button size="small" variant="outlined" color="warning"
            startIcon={<ResetIcon />}
            onClick={() => reset(settingKey)} disabled={isSaving}>
            Reset
          </Button>
        )}
      </Box>
    </Paper>
  );
}

export function KeyValueEditor({ settingKey, label, helperText = '' }) {
  const { values, isCustomized, saving, save, reset, updateValue } = useSettings();
  const val = values[settingKey] || {};
  const isCust = isCustomized(settingKey);
  const isSaving = !!saving[settingKey];

  const updateEntry = (entryKey, newVal) => {
    const updated = { ...val, [entryKey]: Number(newVal) || 0 };
    updateValue(settingKey, updated);
  };

  return (
    <Accordion variant="outlined" sx={{ mb: 2 }}>
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography variant="subtitle2" fontWeight={700} sx={{ fontFamily: T.font }}>{label}</Typography>
          {isCust && <Chip label="Aangepast" size="small" color="info" variant="outlined" />}
        </Box>
      </AccordionSummary>
      <AccordionDetails>
        {helperText && (
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 2 }}>
            {helperText}
          </Typography>
        )}
        <Box sx={{
          display: 'grid',
          gridTemplateColumns: { xs: '1fr 1fr', sm: '1fr 1fr 1fr', md: 'repeat(4, 1fr)' },
          gap: 1.5,
        }}>
          {Object.entries(val).map(([k, v]) => (
            <TextField key={k} size="small" label={k} type="number" value={v}
              onChange={(e) => updateEntry(k, e.target.value)} />
          ))}
        </Box>
        <Box sx={{ display: 'flex', gap: 1, mt: 2 }}>
          <Button size="small" variant="contained"
            startIcon={isSaving ? <CircularProgress size={16} /> : <SaveIcon />}
            onClick={() => save(settingKey)} disabled={isSaving}
            sx={{ bgcolor: T.primary, '&:hover': { bgcolor: T.primaryDim } }}>
            Opslaan
          </Button>
          {isCust && (
            <Button size="small" variant="outlined" color="warning"
              startIcon={<ResetIcon />}
              onClick={() => reset(settingKey)} disabled={isSaving}>
              Reset
            </Button>
          )}
        </Box>
      </AccordionDetails>
    </Accordion>
  );
}

export function SectionHeader({ title, subtitle }) {
  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{
        fontFamily: T.font, fontSize: '1.4rem', fontWeight: 800,
        color: T.text, letterSpacing: '-0.02em',
      }}>
        {title}
      </Typography>
      {subtitle && (
        <Typography sx={{ fontFamily: T.fontBody, fontSize: '0.85rem', color: T.secondary, mt: 0.5 }}>
          {subtitle}
        </Typography>
      )}
    </Box>
  );
}
